import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Text, XStack } from 'tamagui'
import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import { Chip } from '../../../ui'
import type { Employment } from '../../../lib/types'
import { Field } from './shared'

/** Selection is owned by the request owner; late responses stay in their own query cache. */
export function useEmploymentSelection(onBehalfOfUserId?: string) {
  const owner = onBehalfOfUserId ?? ''
  const q = useQuery({
    queryKey: ['edo-employments', owner],
    queryFn: () => api.edoEmployments(onBehalfOfUserId),
    retry: false,
  })
  const jobs = (q.data ?? []).filter(job => job.status === 'ACTIVE')
  const [choice, setChoice] = useState<{ owner: string; id: string } | null>(null)
  useEffect(() => { setChoice(null) }, [owner])
  const primaries = jobs.filter(job => job.isPrimary)
  const defaultJob = jobs.length === 1 ? jobs[0] : primaries.length === 1 ? primaries[0] : undefined
  const selected = choice?.owner === owner ? jobs.find(job => job.id === choice.id) : defaultJob
  return {
    jobs,
    employmentId: selected?.id,
    ready: q.isSuccess && !q.isFetching && (jobs.length === 0 || !!selected),
    loading: q.isPending || q.isFetching,
    error: q.error,
    select: (id: string) => setChoice({ owner, id }),
  }
}
export type EmploymentSelection = ReturnType<typeof useEmploymentSelection>
export function EmploymentField({ selection }: { selection: EmploymentSelection }) {
  const { t } = useApp()
  if (selection.error) return <Text color="$muted">{t({ ru: 'Не удалось загрузить места работы. Откройте форму заново.', az: 'İş yerləri yüklənmədi. Formanı yenidən açın.', en: 'Could not load workplaces. Reopen the form.' })}</Text>
  if (selection.loading) return <Text color="$muted">{t({ ru: 'Загрузка мест работы…', az: 'İş yerləri yüklənir…', en: 'Loading workplaces…' })}</Text>
  if (!selection.jobs.length) return null
  return <Field label={t({ ru: 'Место работы', az: 'İş yeri', en: 'Workplace' })} required={selection.jobs.length > 1}>
    <XStack gap={8} flexWrap="wrap">{selection.jobs.map(job => <Chip key={job.id} on={selection.employmentId === job.id} dim onPress={() => selection.select(job.id)}>{employmentLabel(job)}{job.isPrimary ? t({ ru: ' · основное', az: ' · əsas', en: ' · primary' }) : ''}</Chip>)}</XStack>
    {!selection.ready && <Text color="$muted">{t({ ru: 'Выберите место работы для этой заявки.', az: 'Bu ərizə üçün iş yerini seçin.', en: 'Choose a workplace for this request.' })}</Text>}
  </Field>
}
export function employmentLabel(job: Pick<Employment, 'companyName' | 'position'>) {
  return [job.companyName, job.position].filter(Boolean).join(' · ')
}
